import React, { useEffect, useState } from 'react';
import { projectsApi } from '../services/api';
import { Container, Box, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import { t } from '../localization';
import StatusManager from '../components/StatusManager';
import CustomFieldsManager from '../components/CustomFieldsManager';
import BallotIcon from '@mui/icons-material/Ballot';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [ticketPrefix, setTicketPrefix] = useState('');
  const [visibleToAllUsers, setVisibleToAllUsers] = useState(false);
  
  // Состояние для управления вкладками
  const [tabValue, setTabValue] = useState(0);
  
  // Состояние для управления диалогом настроек статусов
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedProjectForStatuses, setSelectedProjectForStatuses] = useState(null);
  // Состояние для управления диалогом кастомных полей
  const [customFieldsDialogOpen, setCustomFieldsDialogOpen] = useState(false);
  const [selectedProjectForFields, setSelectedProjectForFields] = useState(null);

  const fetchProjects = async () => {
    const res = await projectsApi.getAll();
    setProjects(res.data);
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleOpen = (project = null) => {
    setEditProject(project);
    setName(project ? project.name : '');
    setDescription(project ? project.description : '');
    setTicketPrefix(project ? project.ticketPrefix || '' : '');
    setVisibleToAllUsers(project ? !!project.visibleToAllUsers : false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditProject(null);
    setName('');
    setDescription('');
  };

  const handleSave = async () => {
    if (editProject) {
      await projectsApi.update(editProject.id, { name, description, ticketPrefix, visibleToAllUsers });
    } else {
      await projectsApi.create({ name, description, ticketPrefix, visibleToAllUsers });
    }
    await fetchProjects();
    handleClose();
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('deleteProject'))) {
      await projectsApi.delete(id);
      await fetchProjects();
    }
  };
  
  // Обработчик изменения вкладки
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Открытие диалога настроек статусов
  const handleOpenStatusDialog = (project) => {
    setSelectedProjectForStatuses(project);
    setStatusDialogOpen(true);
  };
  
  // Закрытие диалога настроек статусов
  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    setSelectedProjectForStatuses(null);
  };

  const handleOpenCustomFieldsDialog = (project) => {
    setSelectedProjectForFields(project);
    setCustomFieldsDialogOpen(true);
  };
  const handleCloseCustomFieldsDialog = () => {
    setCustomFieldsDialogOpen(false);
    setSelectedProjectForFields(null);
  };

  return (
    <Container sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">{t('projects')}</Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpen()}>{t('createProject')}</Button>
      </Box>
      
      {/* Вкладки для разделов */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Список проектов" />
          <Tab label="Настройки" disabled={!projects.length} />
        </Tabs>
      </Paper>
      
      {/* Содержимое вкладки "Список проектов" */}
      {tabValue === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('name')}</TableCell>
                <TableCell>{t('description')}</TableCell>
                <TableCell>{t('ticketPrefix')}</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((project) => (
                <TableRow key={project.id}>
                  <TableCell>{project.name}</TableCell>
                  <TableCell>{project.description}</TableCell>
                  <TableCell>{project.ticketPrefix || ''}</TableCell>
                  <TableCell>
                    <Tooltip title="Редактировать проект">
                      <IconButton onClick={() => handleOpen(project)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Настройки статусов">
                      <IconButton onClick={() => handleOpenStatusDialog(project)} color="primary">
                        <SettingsIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Кастомные поля">
                      <IconButton onClick={() => handleOpenCustomFieldsDialog(project)} color="secondary">
                        <BallotIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Удалить проект">
                      <IconButton onClick={() => handleDelete(project.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Содержимое вкладки "Настройки" */}
      {tabValue === 1 && (
        <Typography variant="body1" sx={{ p: 2 }}>
          Выберите проект и нажмите на иконку настроек для управления статусами.
        </Typography>
      )}

      {/* Диалог редактирования проекта */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editProject ? t('editProject') : t('createProject')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('projectName')}
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('description')}
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('ticketPrefix')}
            value={ticketPrefix}
            onChange={e => setTicketPrefix(e.target.value)}
            fullWidth
            margin="normal"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <input
              type="checkbox"
              id="visibleToAllUsers"
              checked={visibleToAllUsers}
              onChange={e => setVisibleToAllUsers(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            <label htmlFor="visibleToAllUsers">{t('visibleToAllUsers')}</label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('cancel')}</Button>
          <Button onClick={handleSave} variant="contained" color="primary">{t('save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог кастомных полей */}
      <Dialog 
        open={customFieldsDialogOpen} 
        onClose={handleCloseCustomFieldsDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Кастомные поля проекта: {selectedProjectForFields?.name}
        </DialogTitle>
        <DialogContent>
          {selectedProjectForFields && (
            <CustomFieldsManager projectId={selectedProjectForFields.id} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCustomFieldsDialog}>Закрыть</Button>
        </DialogActions>
      </Dialog>
      
      {/* Диалог настроек статусов */}
      <Dialog 
        open={statusDialogOpen} 
        onClose={handleCloseStatusDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Настройки статусов проекта: {selectedProjectForStatuses?.name}
        </DialogTitle>
        <DialogContent>
          {selectedProjectForStatuses && (
            <StatusManager projectId={selectedProjectForStatuses.id} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectsPage;
